import { useState } from "react";
import AvailableCustomIntegrations from "./AvailableCustomIntegrations";
import { CommonIntegrationInstance } from "./mcpSlice";
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  Typography,
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { BackButton } from "@/components/button";
import { useAppState } from "@/modules/app/useAppContext";
import CustomIntegrationsSelector from "./CustomIntegrationsSelector";
import CustomIntegrationForm from "./CustomIntegrationForm";
import { CUSTOM_INTEGRATIONS } from "./constants";
import { useBreadcrumb } from "@/components/breadcrumbs";

interface EditingIntegration {
  instance: CommonIntegrationInstance;
  template: (typeof CUSTOM_INTEGRATIONS)[number];
}

const CustomIntegrations = () => {
  useBreadcrumb("Custom Integrations");
  const { previousPath } = useAppState();
  const [showAvailableSheet, setShowAvailableSheet] = useState(false);
  const [showFormSheet, setShowFormSheet] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<
    (typeof CUSTOM_INTEGRATIONS)[number] | null
  >(null);
  const [editingIntegration, setEditingIntegration] = useState<EditingIntegration | null>(null);

  // Handler for "Create new integration" button
  const handleOpenAvailableSheet = () => {
    setShowAvailableSheet(true);
  };

  // Handler for "Create <type> integration" button in AvailableCustomIntegrations
  const handleAddClick = (integration: (typeof CUSTOM_INTEGRATIONS)[number]) => {
    setShowAvailableSheet(false);
    setSelectedIntegration(integration);
    setEditingIntegration(null);
    setShowFormSheet(true);
  };

  const handleEdit = (instance: CommonIntegrationInstance) => {
    const template = CUSTOM_INTEGRATIONS.find(i => i.type === instance.type);
    if (template) {
      setSelectedIntegration(instance as unknown as (typeof CUSTOM_INTEGRATIONS)[number]);
      setEditingIntegration({ instance, template });
      setShowFormSheet(true);
    }
  };

  // Handler for closing the form sheet
  const handleFormClose = () => {
    setSelectedIntegration(null);
    setEditingIntegration(null);
    setShowFormSheet(false);
  };

  return (
    <Stack direction="column">
      <Stack direction="row" className="al-items-center al-gap-4 al-mb-2">
        <BackButton defaultUrl={previousPath || "/datamates"} />
        <Typography variant="page-heading">Custom Integrations</Typography>
        <Button
          variant="default"
          size="sm"
          className="al-ml-auto"
          onClick={handleOpenAvailableSheet}
        >
          Create new integration
        </Button>
      </Stack>
      <p className="al-text-sm al-text-muted-foreground">
        Custom integrations allow you to extend MCP&apos;s functionality with your own code, APIs,
        or servers. Configure them once and they&apos;ll be available across your MCP environment.
      </p>

      <Card>
        <CardHeader>
          <Typography variant="section-heading">Your Integrations</Typography>
        </CardHeader>
        <CardContent>
          <CustomIntegrationsSelector onEditOpen={handleEdit} />
        </CardContent>
      </Card>

      {/* Sheet for AvailableCustomIntegrations */}
      <Sheet open={showAvailableSheet} onOpenChange={setShowAvailableSheet}>
        <SheetContent side="right" className="al-w-[480px]">
          <SheetHeader>
            <SheetTitle>Create a Custom Integration</SheetTitle>
          </SheetHeader>
          <div className="al-mt-4">
            <AvailableCustomIntegrations onAddClick={handleAddClick} />
          </div>
        </SheetContent>
      </Sheet>

      {/* Sheet for CommonIntegrationForm */}
      <Sheet open={showFormSheet} onOpenChange={setShowFormSheet}>
        <SheetContent side="right" className="al-w-[480px]">
          <SheetHeader>
            <SheetTitle>
              {editingIntegration?.template?.name || selectedIntegration?.name}
            </SheetTitle>
          </SheetHeader>
          <div className="al-mt-4">
            {(selectedIntegration || editingIntegration) && (
              <CustomIntegrationForm
                integration={editingIntegration?.template || selectedIntegration!}
                initialConfig={editingIntegration?.instance}
                onClose={handleFormClose}
              />
            )}
          </div>
        </SheetContent>
      </Sheet>
    </Stack>
  );
};

export default CustomIntegrations;
